/**
 * Avatar helpers — derive display initials and a deterministic colour for a
 * company (or any name). Kept pure and React-free so the logic is unit-testable
 * without a component runner; the visual circle lives in components/Avatar.tsx.
 */

/**
 * One to two uppercase initials for a name: the first letters of the first two
 * whitespace-separated words ("Acme Couriers" → "AC"); a single word yields its
 * first two letters ("Acme" → "AC"); blank input yields "?".
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * Deterministic colour palette for avatars. Class pairs are written out in full
 * (never interpolated as `bg-${c}-500`) so Tailwind's JIT compiler keeps them
 * in the bundle — a templated class name would be purged and render colourless.
 */
const AVATAR_PALETTE = [
  'bg-blue-500 text-white',
  'bg-emerald-500 text-white',
  'bg-amber-500 text-white',
  'bg-rose-500 text-white',
  'bg-violet-500 text-white',
  'bg-orange-500 text-white',
  'bg-teal-500 text-white',
  'bg-fuchsia-500 text-white',
] as const;

/**
 * A stable "bg-… text-…" class pair for a name, so the same company always gets
 * the same colour. Uses a small rolling hash over the characters, kept inside
 * 32-bit range with `| 0`, then indexes into the fixed palette.
 */
export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!;
}
